import { useState, useSyncExternalStore, type RefObject } from 'react';

function criarLeitor<E extends string>(
  referencia: RefObject<HTMLElement | null>,
  encaixar: (largura: number) => E,
) {
  return {
    fotografar: (): E => encaixar(referencia.current?.clientWidth ?? 0),

    assinar: (avisar: () => void) => {
      const elemento = referencia.current;
      if (!elemento) return () => undefined;

      let pedido = 0;
      // Fora do retorno do observador: trocar a disposicao muda a altura do elemento, e
      // no mesmo quadro isso dispararia outra rodada de observacao.
      const agendar = () => {
        window.cancelAnimationFrame(pedido);
        pedido = window.requestAnimationFrame(avisar);
      };

      window.addEventListener('resize', agendar);
      const observador =
        typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(agendar);
      observador?.observe(elemento);

      return () => {
        window.cancelAnimationFrame(pedido);
        window.removeEventListener('resize', agendar);
        observador?.disconnect();
      };
    },
  };
}

/**
 * Escolhe a disposicao de um conteudo pela largura que ele recebeu.
 *
 * A medida vem do proprio elemento, e nao da janela, para valer em qualquer combinacao
 * de zoom, tela e menu lateral. So renderiza de novo quando a disposicao muda: arrastar
 * a borda da janela nao custa uma renderizacao por pixel.
 *
 * Antes da primeira medida, e no ambiente de teste, a largura e zero. A troca depois da
 * medida acontece antes da pintura: a pessoa nunca ve a disposicao errada.
 *
 * `encaixar` e lida uma vez so: precisa ser uma funcao pura, fora do componente.
 */
export function useEncaixePelaLargura<E extends string>(
  referencia: RefObject<HTMLElement | null>,
  encaixar: (largura: number) => E,
): E {
  const [leitor] = useState(() => criarLeitor(referencia, encaixar));
  return useSyncExternalStore(leitor.assinar, leitor.fotografar);
}
